import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import { API } from '../App';
import axios from 'axios';
import { MapPin, Users, CheckCircle2, Droplets, Lightbulb, Trash2, Shield } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import PhoneAuth from './PhoneAuth';
import { toast } from 'sonner';
import { Link } from "react-router-dom";

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, login } = useContext(AuthContext);
  const [showAuth, setShowAuth] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/stats`)
      .then(res => setStats(res.data))
      .catch(err => console.error(err));
  }, []);

  const handleAuthSuccess = (token, userData) => {
    login(token, userData);
    setShowAuth(false);
    navigate('/dashboard');
  };

  const categories = [
    { id: 'pothole', icon: '🕳️', className: 'category-pothole' },
    { id: 'road', icon: '🛣️', className: 'category-road' },
    { id: 'water', icon: <Droplets size={24} />, className: 'category-water' },
    { id: 'electricity', icon: <Lightbulb size={24} />, className: 'category-electricity' },
    { id: 'garbage', icon: <Trash2 size={24} />, className: 'category-garbage' },
    { id: 'safety', icon: <Shield size={24} />, className: 'category-safety' },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section flex items-center relative">
        <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              {t('home.title')}
              <br />
              <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                {t('home.subtitle')}
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed">
              {t('home.description')}
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                data-testid="get-started-btn"
                onClick={() => (user ? navigate('/report') : setShowAuth(true))}
                className="btn-primary text-lg px-8 py-4"
              >
                {t('home.getStarted')}
              </button>
              <Link
               to="/reports"
               data-testid="browse-issues-btn"
               className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
              {t('home.browseIssues')}
              </Link>
            </div>

            {/* Stats */}
            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12">
                <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-6 border border-white">
                  <div className="text-3xl font-bold text-gray-900" data-testid="total-reports">{stats.total_reports}</div>
                  <div className="text-sm text-gray-600 mt-1">{t('home.totalReports')}</div>
                </div>
                <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-6 border border-white">
                  <div className="text-3xl font-bold text-green-600" data-testid="completed-reports">{stats.completed_reports}</div>
                  <div className="text-sm text-gray-600 mt-1">{t('home.resolved')}</div>
                </div>
                <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-6 border border-white">
                  <div className="text-3xl font-bold text-blue-600" data-testid="active-reports">{stats.active_reports}</div>
                  <div className="text-sm text-gray-600 mt-1">{t('home.active')}</div>
                </div>
                <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-6 border border-white">
                  <div className="text-3xl font-bold text-cyan-600" data-testid="total-funds">₹{(stats.total_funds / 1000).toFixed(1)}K</div>
                  <div className="text-sm text-gray-600 mt-1">{t('home.raised')}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">{t('home.reportAnyIssue')}</h2>
            <p className="text-lg text-gray-600">{t('home.reportAnyIssueDesc')}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {categories.map((cat) => {
              const disabled = cat.id !== 'pothole';
              return (
                <div
                  key={cat.id}
                  className={`card p-6 text-center group ${
                    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  }`}
                  data-testid={`category-${cat.id}`}
                  onClick={() => {
                    if (disabled) return;
                    navigate('/reports', { state: { category: cat.id } });
                  }}
                >
                  <div
                    className={`category-icon ${cat.className} mx-auto group-hover:scale-110 transition-transform`}
                  >
                    {cat.icon}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {t(`category.${cat.id}`)}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {t(`category.${cat.id}.desc`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">{t('home.howItWorks')}</h2>
            <p className="text-lg text-gray-600">{t('home.howItWorksDesc')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card p-8 text-center" data-testid="step-report">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                1
              </div>
              <MapPin className="mx-auto mb-4 text-cyan-600" size={32} />
              <h3 className="text-xl font-semibold mb-3">{t('home.step1')}</h3>
              <p className="text-gray-600">{t('home.step1Desc')}</p>
            </div>
            <div className="card p-8 text-center" data-testid="step-verify">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                2
              </div>
              <CheckCircle2 className="mx-auto mb-4 text-cyan-600" size={32} />
              <h3 className="text-xl font-semibold mb-3">{t('home.step2')}</h3>
              <p className="text-gray-600">{t('home.step2Desc')}</p>
            </div>
            <div className="card p-8 text-center" data-testid="step-crowdfund">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                3
              </div>
              <Users className="mx-auto mb-4 text-cyan-600" size={32} />
              <h3 className="text-xl font-semibold mb-3">{t('home.step3')}</h3>
              <p className="text-gray-600">{t('home.step3Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">{t('home.ctaTitle')}</h2>
          <p className="text-xl mb-8 text-blue-100">{t('home.ctaDesc')}</p>
          <button
            data-testid="cta-start-btn"
            onClick={() => (user ? navigate('/report') : setShowAuth(true))}
            className="bg-white text-cyan-600 px-10 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-colors"
          >
            {t('home.startReporting')}
          </button>
        </div>
      </section>
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent data-testid="auth-modal">
          <DialogHeader>
            <DialogTitle>{t('auth.loginWithPhone')}</DialogTitle>
          </DialogHeader>
          <PhoneAuth
            onSuccess={handleAuthSuccess}
            onCancel={() => setShowAuth(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;